import React from "react";
import { Grid } from "@mui/material";
import { FormField } from "./FormField";
import { BusinessTypeSelect } from "./BusinessTypeSelect";
import { CountryStateSelect } from "./CountryStateSelect";
import { TimeZoneSelect } from "./TimeZoneSelect";
import { TimeZone } from "../types/authTypes";

// 1. Define supported field types
type FieldType = "text" | "email" | "tel" | "number" | "businessType" | "timeZone" | "countryState" | "date";

// 2. Field schema type
export interface FieldSchema {
  type: FieldType;
  name: string;
  label: string;
  icon?: React.ReactNode;
  required?: boolean;
  grid?: { xs?: number; sm?: number };
  placeholder?: string;
}

// 3. Generic form props
interface GenericFormProps<TFormData, TErrors> {
  schema: FieldSchema[];
  formData: TFormData;
  errors: TErrors;
  updateField: (field: keyof TFormData, value: any) => void;
  clearError: (field: keyof TErrors) => void;
  handleEmailChange: (field: string, value: string) => void;
  handleDobChange: (value: string) => void;
  handlePhoneChange: (field: string, value: string) => void;
  timeZones: TimeZone[];
  timeZonesLoading: boolean;
  inputValue: string;
}

export function GenericForm<TFormData extends Record<string, any>, TErrors extends Record<string, string | undefined>>({
  schema,
  formData,
  errors,
  updateField,
  clearError,
  handleEmailChange,
  handleDobChange,
  handlePhoneChange,
  timeZones,
  timeZonesLoading,
  inputValue,
}: GenericFormProps<TFormData, TErrors>) {
  return (
    <>
      {schema.map((field) => {
        const fieldError = errors[field.name as keyof TErrors];

        switch (field.type) {
          case "businessType":
            return <BusinessTypeSelect key={field.name} value={formData[field.name]} onChange={(val) => updateField(field.name as keyof TFormData, val)} />;

          case "timeZone":
            return (
              <TimeZoneSelect key={field.name} timeZones={timeZones} value={formData[field.name]} onChange={(val) => updateField(field.name as keyof TFormData, val)} loading={timeZonesLoading} />
            );

          case "countryState":
            return (
              <CountryStateSelect
                key={field.name}
                selectedCountry={formData.country}
                selectedState={formData.state}
                onCountryChange={(val) => updateField("country" as keyof TFormData, val)}
                onStateChange={(val) => updateField("state" as keyof TFormData, val)}
              />
            );

          case "date":
            return (
              <FormField
                key={field.name}
                icon={field.icon}
                label={field.label}
                value={inputValue}
                onChange={handleDobChange}
                error={(errors.date_of_birth || errors.day || errors.month) as string}
                placeholder={field.placeholder}
              />
            );

          default:
            const fieldComponent = (
              <FormField
                key={field.name}
                icon={field.icon}
                label={field.label}
                value={formData[field.name]}
                onChange={(val) => {
                  if (field.type === "email") {
                    handleEmailChange(field.name, val);
                  } else if (field.type === "tel") {
                    handlePhoneChange(field.name, val);
                  } else {
                    updateField(field.name as keyof TFormData, val);
                  }
                }}
                error={fieldError}
                type={field.type}
                required={field.required}
                placeholder={field.placeholder}
                onClearError={() => clearError(field.name as keyof TErrors)}
              />
            );

            return field.grid ? (
              <Grid item xs={field.grid.xs} sm={field.grid.sm} key={field.name}>
                {fieldComponent}
              </Grid>
            ) : (
              fieldComponent
            );
        }
      })}
    </>
  );
}
